import * as React from "react";
import AsyncSelect from "react-select/async";
import { cn } from "@/lib/utils";
import { fetchAllCities } from '@features/settings/services/cityService';
import { Button } from "@shared/components/ui/button";

export function MultiSelectCity({
  cities = [],
  onSelect,
  className,
  placeholder = "Select cities...",
  isDisabled = false,
}) {
  const [selectedCities, setSelectedCities] = React.useState(cities || []);
  const [defaultOptions, setDefaultOptions] = React.useState([]);

  React.useEffect(() => {
    setSelectedCities(cities || []);
  }, [cities]);

  // Load default options on component mount
  React.useEffect(() => {
    const loadInitialOptions = async () => {
      try {
        const response = await fetchAllCities('', 1000, 1);
        
        if (response?.cities) {
          const options = response.cities.map((city) => ({
            value: city._id,
            label: city.name,
            city: city,
            ...city
          }));
          setDefaultOptions(options);
        }
      } catch (error) {
        console.error("Error loading default cities:", error);
      }
    };

    loadInitialOptions();
  }, []);

  // Function to load options asynchronously
  const loadOptions = React.useCallback(async (inputValue) => {
    try {
      const response = await fetchAllCities(inputValue || '', 1000, 1);
      
      if (response?.cities) {
        return response.cities.map((city) => ({
          value: city._id,
          label: city.name,
          city: city, // Keep the full city object for later use
          ...city
        }));
      }
      return [];
    } catch (error) {
      console.error("Error loading cities:", error);
      return [];
    }
  }, []);

  // Custom styles for react-select to match your design
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '40px',
      border: '2px solid hsl(var(--border))',
      borderRadius: '6px',
      backgroundColor: 'hsl(var(--background))',
      boxShadow: state.isFocused ? '0 0 0 2px hsl(var(--ring))' : 'none',
      borderColor: state.isFocused ? 'hsl(var(--ring))' : 'hsl(var(--border))',
      '&:hover': {
        borderColor: 'hsl(var(--ring))',
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'hsl(var(--popover))',
      border: '1px solid hsl(var(--border))',
      borderRadius: '6px',
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? 'hsl(var(--accent))' 
        : state.isFocused 
        ? 'hsl(var(--accent) / 0.5)' 
        : 'transparent',
      color: 'hsl(var(--foreground))',
      padding: '8px 12px',
      '&:hover': {
        backgroundColor: 'hsl(var(--accent))',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'hsl(var(--secondary))',
      borderRadius: '4px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'hsl(var(--secondary-foreground))',
      fontSize: '12px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'hsl(var(--secondary-foreground))',
      '&:hover': {
        backgroundColor: 'hsl(var(--destructive))',
        color: 'hsl(var(--destructive-foreground))',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'hsl(var(--muted-foreground))',
    }),
    input: (provided) => ({
      ...provided,
      color: 'hsl(var(--foreground))',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'hsl(var(--foreground))',
    }),
  };

  // Format option display
  const formatOptionLabel = (option) => (
    <div className="flex flex-col py-1">
      <div className="flex items-center space-x-2">
        <span className="font-medium text-foreground">
          {option.name}
        </span>
      </div>
      <div className="flex items-center space-x-2 text-xs text-muted-foreground">
        <span>{option.nameArabic || option.province || option.state}</span>
        {option.country && (
          <span className="px-2 py-1 bg-muted rounded text-xs">
            {option.country}
          </span>
        )}
      </div>
    </div>
  );

  // Handle selection change
  const handleChange = (selectedOptions) => {
    const cities = selectedOptions ? selectedOptions.map(option => option.city || option) : [];
    setSelectedCities(cities);
    onSelect(cities);
  };

  // Handle select all functionality
  const handleSelectAll = () => {
    const allCities = defaultOptions.map(option => option.city || option);
    setSelectedCities(allCities);
    onSelect(allCities);
  };

  // Handle clear all functionality
  const handleClearAll = () => {
    setSelectedCities([]);
    onSelect([]);
  };

  // Convert cities to options format
  const cityOptions = selectedCities.map(city => ({
    value: city._id,
    label: city.name,
    city: city,
    ...city
  }));

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1">
          <AsyncSelect
            isMulti
            value={cityOptions}
            loadOptions={loadOptions}
            defaultOptions={defaultOptions}
            onChange={handleChange}
            placeholder={placeholder}
            isDisabled={isDisabled}
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            cacheOptions
            loadingMessage={() => "Loading cities..."}
            noOptionsMessage={({ inputValue }) => 
              inputValue.length === 0 
                ? "No cities available" 
                : "No cities found matching your search"
            }
            className="react-select-container"
            classNamePrefix="react-select"
            isClearable={false}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            getOptionValue={(option) => option.value}
            menuPortalTarget={document.body}
            menuPosition="absolute"
          />
        </div>
        
        {defaultOptions.length > 0 && (
          <div className="flex gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              disabled={isDisabled || selectedCities.length === defaultOptions.length}
              className="text-xs whitespace-nowrap"
            >
              Select All
            </Button>
            {selectedCities.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                disabled={isDisabled}
                className="text-xs"
              >
                Clear
              </Button>
            )}
          </div>
        )}
      </div>
      
      {selectedCities.length > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {selectedCities.length} cit{selectedCities.length !== 1 ? "ies" : "y"} selected
            {defaultOptions.length > 0 && ` of ${defaultOptions.length}`}
          </span>
        </div>
      )}
    </div>
  );
}
